import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Search, Printer, FileDown, ChevronLeft, ChevronRight } from 'lucide-react'
import toast from 'react-hot-toast'

import { PageHeader } from '@/components/shared/PageHeader'
import { EmptyState } from '@/components/shared/EmptyState'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { SearchableSelect, SearchableSelectOption } from '@/components/ui/searchable-select'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useAuthStore } from '@/store/auth.store'
import { branchService } from '@/services/branchService'
import { financeService } from '@/services/financeService'
import { reportService, SalesRevenueEntry } from '@/services/reportService'
import { BranchEntity, FinanceCustomer } from '@/api/types'
import { formatCurrency } from '@/lib/format'

export const SalesRevenueReport: React.FC = () => {
  const { t } = useTranslation('common')
  const { user } = useAuthStore()
  const navigate = useNavigate()

  const [rows, setRows] = useState<SalesRevenueEntry[]>([])
  const [loading, setLoading] = useState(false)
  const [branches, setBranches] = useState<BranchEntity[]>([])
  const [customers, setCustomers] = useState<FinanceCustomer[]>([])
  const [searchQuery, setSearchQuery] = useState('')

  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [totalCount, setTotalCount] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [totals, setTotals] = useState({
    totalAmount: 0,
  })

  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [selectedBranchId, setSelectedBranchId] = useState<number | undefined>(undefined)
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | undefined>(undefined)

  useEffect(() => {
    const loadBranches = async () => {
      try {
        const branchList = await branchService.getAllBranches()
        setBranches(branchList || [])
        if (user?.selectedBranch?.branchId && !selectedBranchId) {
          const branchExists = branchList?.some((b) => b.id === user.selectedBranch?.branchId)
          if (branchExists) {
            setSelectedBranchId(user.selectedBranch.branchId)
          }
        }
      } catch {
        // Error handled silently
      }
    }
    void loadBranches()
  }, [])

  useEffect(() => {
    if (!selectedBranchId) {
      setCustomers([])
      setSelectedCustomerId(undefined)
      return
    }

    const loadCustomers = async () => {
      try {
        const customerList = await financeService.getAllCustomers(selectedBranchId)
        setCustomers(customerList || [])
        setSelectedCustomerId(undefined)
      } catch {
        setCustomers([])
      }
    }
    void loadCustomers()
  }, [selectedBranchId])

  useEffect(() => {
    const today = new Date()
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1)
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0)
    setFromDate(formatDateForInput(firstDay))
    setToDate(formatDateForInput(lastDay))
  }, [])

  const formatDateForInput = (date: Date): string => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-'
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    } catch {
      return dateString
    }
  }

  const formatAmount = (amount?: number) => formatCurrency(Number(amount ?? 0))

  const handleSearch = async (resetPage: boolean = false) => {
    if (!selectedBranchId) {
      toast.error('Please select a branch')
      return
    }
    if (!fromDate || !toDate) {
      toast.error('Please select both from date and to date')
      return
    }

    try {
      setLoading(true)
      const currentPage = resetPage ? 1 : pageNumber
      if (resetPage) setPageNumber(1)

      const response = await reportService.getSalesRevenueReport({
        fromDate: `${fromDate}T00:00:00`,
        toDate: `${toDate}T23:59:59`,
        branchId: selectedBranchId,
        pageNumber: currentPage,
        pageSize,
        customerId: selectedCustomerId !== undefined ? selectedCustomerId : 0,
      })
      setRows(response.data || [])
      setTotalCount(response.totalRecords || 0)
      setTotals({
        totalAmount: response.totalAmount ?? 0,
      })
      setTotalPages(response.totalRecords ? Math.ceil(response.totalRecords / pageSize) : 0)
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to load sales revenue report')
      setRows([])
      setTotalCount(0)
      setTotalPages(0)
      setTotals({ totalAmount: 0 })
    } finally {
      setLoading(false)
    }
  }

  const filteredData = useMemo(() => {
    if (!searchQuery.trim()) return rows
    const query = searchQuery.toLowerCase()
    return rows.filter((entry) =>
      (entry.customer || '').toLowerCase().includes(query)
      || (entry.description || '').toLowerCase().includes(query)
      || (entry.type || '').toLowerCase().includes(query),
    )
  }, [rows, searchQuery])

  useEffect(() => {
    if (selectedBranchId && fromDate && toDate && pageNumber > 1 && rows.length > 0) {
      void handleSearch(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNumber])

  const branchOptions: SearchableSelectOption[] = branches.map((branch) => {
    const branchId = branch.id || (branch as any).branchId || 0
    return {
      value: String(branchId),
      label: branch.nameEn || branch.nameAr || `Branch ${branchId}`,
    }
  })

  const customerOptions: SearchableSelectOption[] = [
    { value: '', label: t('reports.allCustomers') },
    ...customers.map((customer) => ({
      value: String(customer.customerId),
      label: customer.customerNameEn || customer.customerNameAr || `Customer ${customer.customerId}`,
    })),
  ]

  const handlePrint = () => {
    if (!selectedBranchId || !fromDate || !toDate) {
      toast.error('Please select branch and date range before printing')
      return
    }
    const params = new URLSearchParams({
      fromDate,
      toDate,
      branchId: String(selectedBranchId),
    })
    if (selectedCustomerId) params.append('customerId', String(selectedCustomerId))
    window.open(`/reports/sales-revenue/print?${params.toString()}`, '_blank')
  }

  const handleCsv = () => {
    if (filteredData.length === 0) {
      toast.error('No data to export')
      return
    }
    const headers = ['Date', 'Customer', 'Description', 'Amount'].join(',')
    const csvRows = filteredData.map((entry) => [
      `"${formatDate(entry.createdDate)}"`,
      `"${entry.customer || ''}"`,
      `"${entry.description || ''}"`,
      `"${formatAmount(entry.amount)}"`,
    ].join(','))
    const totalRow = [
      '"Total"',
      '""',
      '""',
      `"${formatAmount(totals.totalAmount)}"`,
    ].join(',')
    const blob = new Blob([[headers, ...csvRows, totalRow].join('\n')], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `sales-revenue-${fromDate}_${toDate}.csv`
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    toast.success('CSV file downloaded')
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={t('navigation.salesRevenueReport')}
        description={t('reports.viewSalesRevenueReport')}
        action={{
          label: t('reports.print'),
          onClick: handlePrint,
          icon: Printer,
        }}
      />

      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>{t('reports.fromDate')}</Label>
              <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>{t('reports.toDate')}</Label>
              <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>{t('reports.branch')}</Label>
              <SearchableSelect
                options={branchOptions}
                value={selectedBranchId ? String(selectedBranchId) : ''}
                onValueChange={(value) => {
                  setSelectedBranchId(value ? Number(value) : undefined)
                  setSelectedCustomerId(undefined)
                }}
                placeholder={t('reports.selectBranch')}
                searchPlaceholder={t('reports.searchBranches')}
                emptyMessage={t('reports.noBranchesFound')}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('reports.customer')}</Label>
              <SearchableSelect
                options={customerOptions}
                value={selectedCustomerId ? String(selectedCustomerId) : ''}
                onValueChange={(value) => setSelectedCustomerId(value ? Number(value) : undefined)}
                placeholder={t('reports.allCustomers')}
                searchPlaceholder={t('reports.searchCustomers')}
                emptyMessage={t('reports.noCustomersFound')}
                disabled={!selectedBranchId}
              />
            </div>
          </div>
          <div className="mt-4 flex flex-col sm:flex-row gap-4 items-end">
            <div className="flex-1 w-full">
              <Label>{t('reports.search')}</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  placeholder={t('reports.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => void handleSearch(true)} disabled={loading}>
                {loading ? t('reports.loading') : t('reports.search')}
              </Button>
              <Button variant="outline" size="sm" onClick={handleCsv}>
                <FileDown className="h-4 w-4 mr-2" />
                {t('reports.csv')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
              <p className="mt-4 text-muted-foreground">Loading report...</p>
            </div>
          ) : filteredData.length === 0 ? (
            <div className="p-12">
              <EmptyState
                title="No data found"
                description="Click Search to load sales revenue report"
              />
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary">
                        {t('reports.date')}
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary">
                        {t('reports.customer')}
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary">
                        {t('reports.description')}
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary">
                        {t('reports.amount')}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredData.map((entry, index) => (
                      <TableRow key={index} className="border-b hover:bg-primary/5 transition-colors">
                        <TableCell className="px-4 py-2 text-sm text-center">{formatDate(entry.createdDate)}</TableCell>
                        <TableCell className="px-4 py-2 text-sm text-center">{entry.customer || '-'}</TableCell>
                        <TableCell className="px-4 py-2 text-sm text-center">
                          {entry.voucher > 0 ? (
                            <button
                              type="button"
                              onClick={() => navigate(`/general-entries/${entry.voucher}/view`, { state: { readOnly: true } })}
                              className="cursor-pointer text-primary hover:underline"
                            >
                              {entry.description || '-'}
                            </button>
                          ) : (
                            entry.description || '-'
                          )}
                        </TableCell>
                        <TableCell className="px-4 py-2 text-sm text-center">{formatAmount(entry.amount)}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="border-t-2 border-primary/20 bg-primary/5 font-bold">
                      <TableCell colSpan={3} className="px-4 py-2 text-sm text-center">Total</TableCell>
                      <TableCell className="px-4 py-2 text-sm text-center">{formatAmount(totals.totalAmount)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>

              <div className="border-t px-4 sm:px-6 py-4 flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="text-sm text-muted-foreground text-center sm:text-left">
                  Showing {(pageNumber - 1) * pageSize + 1} to {Math.min(pageNumber * pageSize, totalCount)} of {totalCount} entries
                </div>
                <div className="flex items-center gap-3 flex-wrap justify-center">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground whitespace-nowrap">Page Size:</span>
                    <Select
                      value={String(pageSize)}
                      onValueChange={(newSize) => {
                        setPageSize(Number(newSize))
                        setPageNumber(1)
                        if (selectedBranchId && fromDate && toDate) {
                          setTimeout(() => void handleSearch(true), 0)
                        }
                      }}
                    >
                      <SelectTrigger className="w-[100px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="20">20</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPageNumber((prev) => Math.max(prev - 1, 1))}
                    disabled={pageNumber === 1 || loading}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    <span className="hidden sm:inline ml-1">Previous</span>
                  </Button>
                  <div className="text-sm whitespace-nowrap px-2">
                    Page {pageNumber} of {totalPages || 1}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPageNumber((prev) => Math.min(prev + 1, totalPages || 1))}
                    disabled={pageNumber >= totalPages || loading}
                  >
                    <span className="hidden sm:inline mr-1">Next</span>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
